// Self-hosted runtime, declared ONCE for the whole app. We deliberately do NOT use
// `FilesetResolver.forVisionTasks()`, whose default is a jsdelivr CDN — a candidate's browser
// would then reach a third party mid-interview (a new vendor to disclose, one more thing a
// corporate proxy can block, and the candidate's IP handed to a CDN). `?url` makes Vite emit
// these onto our own origin and the MediaPipe tasks accept the paths directly.
import wasmLoaderUrl from "@mediapipe/tasks-vision/vision_wasm_internal.js?url";
import wasmBinaryUrl from "@mediapipe/tasks-vision/vision_wasm_internal.wasm?url";

/**
 * Candidate · the ONE MediaPipe vision runtime.
 *
 * Two features on the interview screen want on-device vision: background blur (selfie
 * segmentation) and camera presence (face detection). Both used to name the wasm URLs themselves,
 * which meant two modules each believing they owned an 11.5 MB download (3.4 MB gzipped) plus its
 * model. This file is the single owner, so:
 *
 *   • ONE pair of URLs. Vite emits one copy of the runtime into /assets/*, both tasks request the
 *     same path, and the browser's HTTP cache treats the second request as a hit.
 *   • ONE download when both start close together. `createVisionTask` fetches the wasm binary once
 *     and hands the same bytes to each task through Emscripten's own `Module.wasmBinary` knob
 *     (MediaPipe honours a pre-set `self.Module`), so a candidate who switches blur on while
 *     presence is still loading does not pull 11.5 MB twice.
 *   • ONE AT A TIME. Instantiation is serialised. Two Emscripten heaps materialising in the same
 *     tick is what makes a mid-range phone stutter — or drop the tab — during an interview.
 *
 * WHAT THIS HONESTLY CANNOT DO: share a single wasm HEAP between two tasks. MediaPipe's public API
 * builds one Emscripten instance per task and exposes no seam to reuse it, so blur + presence
 * together still cost two heaps. The mitigation that actually matters is therefore upstream, in
 * the callers: neither task is created unless it can be USED — the segmenter only when the
 * candidate switches blur on, the face detector only when the camera is genuinely live and
 * presence is wanted. A candidate who denied the camera downloads none of this.
 */

/** The two paths every vision task is created from. Structural — matches MediaPipe's WasmFileset. */
export interface VisionFileset {
  wasmLoaderPath: string;
  wasmBinaryPath: string;
}

export const VISION_FILESET: VisionFileset = {
  wasmLoaderPath: wasmLoaderUrl,
  wasmBinaryPath: wasmBinaryUrl,
};

/**
 * The runtime bytes, shared only for as long as something is still loading.
 *
 * Held while instantiations overlap (so the second task costs no network at all) and dropped the
 * moment the last one finishes — an 11.5 MB ArrayBuffer pinned for the whole interview would be a
 * worse bug than the duplicate fetch it saves. A blur toggle minutes later re-reads the URL, which
 * by then is a cache hit.
 */
let sharedBinary: Promise<ArrayBuffer | null> | null = null;
let inFlight = 0;

/** Everything here is best-effort: a failed prefetch just means MediaPipe fetches it itself. */
function visionWasmBinary(): Promise<ArrayBuffer | null> {
  sharedBinary ??= (async () => {
    try {
      if (typeof fetch !== "function") return null;
      const res = await fetch(wasmBinaryUrl);
      return res.ok ? await res.arrayBuffer() : null;
    } catch {
      return null;
    }
  })();
  return sharedBinary;
}

/** Serialises instantiation. Rejections must not poison the chain, hence the swallowed catch. */
let queue: Promise<unknown> = Promise.resolve();

/**
 * Create a MediaPipe vision task on the shared runtime.
 *
 * `create` receives the fileset and does the task-specific `createFromOptions` call. Anything it
 * throws is the caller's to handle — every caller here degrades to "the feature is off", never to
 * a broken interview.
 */
export async function createVisionTask<T>(create: (fileset: VisionFileset) => Promise<T>): Promise<T> {
  const run = queue.then(
    () => instantiate(create),
    () => instantiate(create),
  );
  queue = run.catch(() => undefined);
  return run;
}

async function instantiate<T>(create: (fileset: VisionFileset) => Promise<T>): Promise<T> {
  inFlight += 1;
  const scope = globalThis as { Module?: unknown };
  try {
    const bytes = await visionWasmBinary();
    // MediaPipe reads `self.Module` as the Emscripten options object when it is set, and clears it
    // straight after. Pre-seeding the already-downloaded binary is the only supported way to stop
    // the second task fetching the runtime again.
    if (bytes) scope.Module = { wasmBinary: bytes };
    return await create(VISION_FILESET);
  } finally {
    // MediaPipe normally clears this itself; on a failed creation it would otherwise linger and
    // leak our bytes into whatever loads next.
    scope.Module = undefined;
    inFlight -= 1;
    if (inFlight === 0) sharedBinary = null;
  }
}
